import {
  Box,
  Button,
  Menu,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { black1, grey, grey10, orangeDefault, redDefault, white, green } from 'app/components/ThemeProvider/colors';
import TableDotsIcon from 'app/components/Icons/TableDotsIcon';
import usePaymentHistory from 'app/pages/SalaryPage/PaymentsHistory/usePaymentHistory';
import {
  useGetSalaryHistory,
  useGetSalaryHistoryFile,
  useUpdateSalaryHistoryRepayment,
} from 'app/utils/services/salaryServices';
import DetailsModal from 'app/pages/SalaryPage/PaymentsHistory/DetailsModal';
import InfoIcon from 'app/components/Icons/InfoIcon';
import PrintIcon from 'app/components/Icons/PrintIcon';
import { serverUrl } from 'app/constants/enviroments';
import LoaderFullScreen from 'app/components/LoaderFullScreen';
import { usePagination } from 'app/utils/customHooks/usePagination';
import Return from 'app/components/Icons/Return';

import { tableCell, tableRowStyle, secondaryText, amountText, CustomMenuItem } from './PaymentsHistory.sx';
import { pagination } from '../SalaryPage.sx';

interface Props {
  bg?: boolean;
  loader?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
}

const PaymentsHistory = (props: Props) => {
  const { startDate, endDate, bg, search } = props;
  const getSalaryFile = useGetSalaryHistoryFile();

  const { handleChange: handlePageChange, page } = usePagination();

  const {
    activeAnchor,
    anchorEl,
    handleMenuClick,
    handleClose,
    tableHeader,
    openDetailsModal,
    setOpenDetailsModal,
    selectedTrnId,
  } = usePaymentHistory();

  const handleDownload = () => {
    getSalaryFile.mutate(selectedTrnId, {
      onSuccess: (fileDownloadData: any) => {
        const url = `${serverUrl}${fileDownloadData?.print_forms[0]?.fileLink}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = fileDownloadData?.print_forms[0]?.fileName;
        window?.document.body.appendChild(link);
        link.click();

        window?.document.body.removeChild(link);
      },
    });
  };

  const { data, refetch, isFetching, isLoading } = useGetSalaryHistory({
    page_id: page,
    search,
    start_date: startDate,
    end_date: endDate,
  });

  const updateSalaryHistoryRepayment = useUpdateSalaryHistoryRepayment();

  const sendAgainToPayment = () => {
    updateSalaryHistoryRepayment.mutate(
      {
        form_id: 'salary_details',
        trn_id: selectedTrnId,
      },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  const details = [
    {
      title: 'Detallara bax',
      icon: <InfoIcon />,
      action: () => setOpenDetailsModal(true),
      type: 'details',
    },
    {
      title: 'Çap et',
      icon: <PrintIcon />,
      action: () => handleDownload(),
      type: 'print_form',
    },
    {
      title: 'İcra edilməyənləri yenidən ödə',
      icon: <Return />,
      action: () => sendAgainToPayment(),
      sx: { color: green },
      type: 'repayment',
    },
  ];

  const currentMenuItem = data?.payments?.find((item) => item.trn_id === selectedTrnId);

  return (
    <>
      {getSalaryFile?.isLoading && <LoaderFullScreen open={getSalaryFile?.isLoading} />}
      <TableContainer
        sx={{ boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.06)', borderRadius: '8px', marginTop: '33px' }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: white,
                padding: '0 28px 0 31px',
              }}
            >
              {tableHeader.map((item) => (
                <TableCell
                  key={item.id}
                  sx={{
                    p: '32px',
                    border: 'none',
                  }}
                >
                  <Typography sx={{ fontSize: '15px', color: grey, lineHeight: '1' }}>{item.name}</Typography>
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  p: '32px',
                  border: 'none',
                }}
              >
                {data?.refresh_button?.enabled && (
                  <Button variant="text" onClick={() => refetch()} sx={{ paddingRight: 0 }}>
                    <Typography sx={{ fontSize: '15px', color: grey, lineHeight: '1' }}>
                      {data?.refresh_button?.text}
                    </Typography>
                    <img
                      src={data?.refresh_button?.icon}
                      width={20}
                      height={20}
                      alt="refresh"
                      className={isFetching ? 'spin' : ''}
                    />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.payments?.map((row, index) => (
              <TableRow
                key={row.trn_id}
                sx={
                  !bg
                    ? (index + 1) % 2 !== 0
                      ? {
                          ...tableRowStyle,
                          background: grey10,
                        }
                      : { ...tableRowStyle, background: white }
                    : (index + 1) % 2 !== 0
                    ? { ...tableRowStyle, background: white }
                    : { ...tableRowStyle, background: grey10 }
                }
              >
                <TableCell sx={{ ...tableCell }}>
                  <Typography sx={{ color: black1, lineHeight: '1' }} fontWeight={600}>
                    {row.description}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...tableCell, paddingRight: '50px' }}>
                  <Typography sx={{ color: black1, lineHeight: '1' }} fontWeight={600}>
                    {row.date}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...tableCell }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: '36px' }}>
                    <Box sx={{ gap: '4px' }}>
                      <Typography sx={secondaryText}>İcra edilən</Typography>
                      <Typography sx={amountText}>{row.amount_closed} </Typography>
                    </Box>
                    <Box sx={{ gap: '4px' }}>
                      <Typography sx={secondaryText}>İcradadır</Typography>
                      <Typography sx={{ ...amountText, color: orangeDefault }}>{row.amount_inprocess} </Typography>
                    </Box>
                    <Box sx={{ gap: '4px' }}>
                      <Typography sx={secondaryText}>İcra edilməyən</Typography>
                      <Typography sx={{ ...amountText, color: redDefault }}>{row.amount_declined} </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    border: 'none',
                  }}
                >
                  <TableDotsIcon
                    sx={{ '&:hover': { cursor: 'pointer' } }}
                    onClick={(event: any) => handleMenuClick(event, row.trn_id)}
                    aria-controls={anchorEl ? `actions-${row.trn_id}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={anchorEl ? 'true' : undefined}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <Menu
            id={activeAnchor}
            disableAutoFocusItem
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{ '& .MuiPaper-root': { right: '30px', left: 'auto !important' } }}
          >
            {details
              ?.filter((item) => currentMenuItem?.actions?.includes(item.type))
              ?.map((detail) => (
                <CustomMenuItem
                  key={detail.title}
                  onClick={detail.action}
                  disabled={detail.type === 'print_form' && getSalaryFile?.isLoading}
                  disableHover={detail.type === 'repayment'}
                >
                  {detail.icon} {detail.title}
                </CustomMenuItem>
              ))}
          </Menu>
        </Table>
        <DetailsModal trn_id={selectedTrnId} open={openDetailsModal} refetch={refetch} close={setOpenDetailsModal} />
      </TableContainer>
      {data?.total_pages > 1 && (
        <Pagination
          sx={{ ...pagination, marginTop: 4 }}
          count={data?.total_pages}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
        />
      )}
    </>
  );
};

export default PaymentsHistory;
